import {ColumnStore, DateHelper, FieldFilterPicker, MessageDialog, List} from "@bryntum/schedulerpro";
import {WIDGET_KEYS} from "../../../constants/WidgetKeys.jsx";
import {STATE_ID_TO_VIEW_NAME} from "./ViewStateGridMapping.jsx";
import {SWIVU_STANDARD_ICONS} from "../../../constants/IconKeys.jsx";
import { defaultCellRenderer, isObjDeepEqual, pickKeysInObject } from "../../../utils/utils.jsx";
import ViewViewStateProcessor from "../viewStateProcessors/ViewViewStateProcessor.js";
import { PLUGINS_KEYS } from "../../../constants/PluginKeys.jsx";

const REMOVE_BUTTON_CLS = "swivu-remove-inspect-filter-button";

const assignIdToItems = (items) => items.map((item, idx) => ({ ...item, pathIdx: idx }));

const getSorters = (sorters) => assignIdToItems(sorters).filter((sorter) => !sorter.hidden);
const getFilters = (filters) => assignIdToItems(filters).filter((filter) => (!filter.hidden && (filter.property || filter.value)));
const getGroupers = (groupers) => assignIdToItems(groupers);

// The filter value can be of combo field value, in that case we need to retrieve the value from the relevant store.
const getFilterValue = (filterItem) => {
  if (!filterItem.value) {
    // Look up this filter among operators
    const filter = FieldFilterPicker.defaultOperators[filterItem.type].find((f) => f.value === filterItem.operator);

    // Filter should always be found, because that's what we normally pick from the filter combo
    if (filter) {
      return FieldFilterPicker.L(filter.text);
    }
    else {
      console.warn("Cannot resolve value for a filter");
      return "";
    }
  }

  if (filterItem.type === "date" && filterItem.value) {
    return DateHelper.format(filterItem.value, "YYYY-MM-DD");
  }

  return filterItem.value;
}

// most of the columns defined in column config doesn't have `text` property instead that's defined in column custom definition
// we're getting column text from column definition in case column didn't have text defined
const getColumnNameByField = (gridColumns, field) => {
  // field can be nested property such as budget.accountingNumber in this case we need to
  const nestedFieldValue = field && field.includes(".") ? field.split(".")[1] : field;
  // still we need to check first with default field value and then with nestedFieldValue as some grid may support nested one's
  const column = gridColumns.find((column) => column.field === field || column.field === nestedFieldValue);
  if (column) {
    if (column.text) {
      return column.text;
    } else if (column.type) {
      return ColumnStore.getColumnClass(column.type)?.defaults?.text;
    }
  } else {
    const columnByDefinition = gridColumns.find((column) => {
      if (column.type) {
        const columnDefinition = ColumnStore.getColumnClass(column.type);
        return columnDefinition?.defaults?.field === field;
      }
    });
    if (columnByDefinition) {
      return ColumnStore.getColumnClass(columnByDefinition.type)?.defaults?.text;
    }
  }
}

const loadFilters = (id, gridType, gridColumns, store, viewType) => {
  return getFilters((store.filters || [])).map((filterItem) => {
    const columnName = getColumnNameByField(gridColumns, filterItem.property) || "?";
    const gridColumnFilterValue = `Filter on "${getFilterValue(filterItem)}"`
    return ({
      itemPath: { path: id, pathIdx: filterItem.pathIdx, id: filterItem.id, type: "filter" },
      gridName: gridType,
      gridColumn: columnName,
      gridColumnFilterValue,
      isDisabled: filterItem.disabled,
      isCaseSensitive: filterItem.type === "string" && filterItem.value ? filterItem.caseSensitive: false,
      viewType,
    });
  })
};
const loadSorters = (id, gridType, gridColumns, store, viewType) => {
  return getSorters((store.sorters || [])).map((sorterItem) => {
    const columnName = getColumnNameByField(gridColumns, sorterItem.field) || "?";
    const isAscending = sorterItem.ascending;
    const gridColumnFilterValue = `Sort ${isAscending ? "Ascending": "Descending"}`;
    return ({
      itemPath: { path: id, pathIdx: sorterItem.pathIdx, field: sorterItem.field, type: "sorter" },
      gridName: gridType,
      gridColumn: columnName,
      gridColumnFilterValue,
      viewType,
    });
  });
};

const loadGroupers = (id, gridType, gridColumns, store, viewType) => {
  return getGroupers(store.groupers || []).map((grouperItem) => {
    const columnName = getColumnNameByField(gridColumns, grouperItem.field) || "?";
    const isStatefulGrouper = grouperItem.custom;
    const gridColumnFilterValue = isStatefulGrouper
      ? "Group by Settings"
      : grouperItem.ascending
        ? "Group Ascending"
        : "Group Descending";
    return ({
      itemPath: { path: id, pathIdx: grouperItem.pathIdx, field: grouperItem.field, type: "grouper" },
      gridName: gridType,
      gridColumn: columnName || "?",
      gridColumnFilterValue,
      viewType,
    })
  });
};

const viewViewStateProcessorColumnsKeys = ViewViewStateProcessor.statesToIncludeArray.find(item => item.name === "columns").statesToInclude;

const cleanColumnsForViewState = (columns) => {
  return columns.map(col =>  pickKeysInObject(col, viewViewStateProcessorColumnsKeys))
}

const loadColumnChanges = (id, gridType, viewType, context) => {
  const { viewManager } = context;
  const changes = [];
  if (viewManager.pageViewsRef && viewManager.pageViewsRef[viewType]) {
    const widget = viewManager.pageViewsRef[viewType];
    const defaultState = cleanColumnsForViewState(widget.defaultState.columns);
    const currentState = cleanColumnsForViewState(widget.state.columns);

    const diff = !isObjDeepEqual(defaultState, currentState);
    if (diff) {
      changes.push({
        itemPath: { path: id, type: "column-changes"},
        gridName: gridType,
        gridColumn: `Miscellaneous`,
        gridColumnFilterValue: "Columns Modified",
        viewType,
      })
    }
  }
  return changes;
}
const timelineKeysInFeatures = [PLUGINS_KEYS.swivuTimelineConfigurator, "timeRanges"];

const loadTimeLineChanges = (id, gridType, viewType, context) => {
  const { viewManager } = context;
  const changes = [];
  if (viewManager.pageViewsRef && viewManager.pageViewsRef[viewType]) {
    const widget = viewManager.pageViewsRef[viewType];
    if (widget.isSchedulerPro && widget.features) { // Even if the grid is scheduler grid, it must have features enabled
      const defaultState = widget.defaultState;
      const currentState = widget.state;

      const changeConfig = {
        gridName: gridType,
        gridColumn: `Miscellaneous`,
        viewType,
      }

      if (defaultState.zoomLevel !== currentState.zoomLevel) {
        changes.push({
          ...changeConfig,
          itemPath: { path: id, type: "timeline-zoom-level-change"},
          gridColumnFilterValue: "Zoom level Modified",
        })
      }

      if (defaultState.tickSize !== currentState.tickSize) {
        changes.push({
          ...changeConfig,
          itemPath: { path: id, type: "timeline-tick-size-change"},
          gridColumnFilterValue: "Ticksize Modified",
        })
      }

      /* Here, we are extracting the 'default state' from timelineConfigurator plugin, because the widget's config is changed by this plugin. */
      if (currentState.features) { // Check if grid has features
        /* Here, we are extracting the 'features' object from state but with limited keys defined in the 'timelineKeysInFeatures' array, we want to check if only those values have changed */
        const cleanedDefaultTimelineState = pickKeysInObject(widget.swivuSchedulerDefaultState.features, timelineKeysInFeatures);
        const cleanedCurrentTimelineState = pickKeysInObject(currentState.features, timelineKeysInFeatures);

        if (!isObjDeepEqual(cleanedDefaultTimelineState, cleanedCurrentTimelineState)) {
          changes.push({
            ...changeConfig,
            itemPath: { path: id, type: "timeline-features-change"},
            gridColumnFilterValue: "Timeline Configuration Modified",
          })
        }
      }
    }
  }
  return changes;
}

const getItemTemplateString = (template) => {
  return `
  <div class="swivu-view-inspector-field w-full border-t-0">
    ${template}
    <button class="${REMOVE_BUTTON_CLS} hover:text-[#e98906] transition !justify-center text-blackdark:text-white bg-transparent border-none cursor-pointer">
      <i class="fa-solid fa-circle-xmark"></i>
    </button>
  </div>
  `;
}

export class ViewStateInspectionView extends List {

  static get $name() {
    return "ViewStateInspectionView";
  }

  static get type() {
    return WIDGET_KEYS.swivuViewStateInspectionView;
  }

  static get configurable() {
    return {
      width: "100%",
      height: "100%",
      multiSelect: false,
      collapsibleGroups: false,
      activateOnMouseover: false,
      allowGroupSelect: false,
      cls: "swivu-view-inspection-view !bg-transparent",
      getItemCls: () => "!py-0 !px-0",
      listeners: {
        item: "handleItemClick"
      },
      groupHeaderTpl: (_, groupName) => {
        const icon = SWIVU_STANDARD_ICONS[groupName.toLowerCase()];
        return getItemTemplateString(`<span class="text-[#e98906] w-[85%] inline-block pl-[1.5em]"><i class="fa-light fa-${icon}" style="margin-inline-end:.7em"></i><b class="capitalize">${groupName.replace("-", " ")}</b></span>`, true)
      },
      itemTpl: (item) => getItemTemplateString(`<span class="text-wrap leading-5 w-[85%] inline-block pl-[1.5em]">${item.gridColumn ? `${item.gridColumn}: ${item.gridColumnFilterValue}`: "No View State"}</span>`),
      store: {
        fields: ["gridColumn", "gridColumnFilterValue", "gridName", "itemPath", "viewType"],
        groupers: [
          { field: 'viewType', ascending: true }
        ],
      }
    }
  }

  updateGridData(viewStates) {
    const list = this;
    const viewStateData = viewStates.reduce((accum, vs) => {
      const columns = vs.gridColumns.reduce((columnsWithChildren, column) => columnsWithChildren.concat(column.children || column), []);
      const sorters = loadSorters(`${vs.id}.sorters`, vs.gridType, columns, vs.store, vs.viewType);
      const filters = loadFilters(`${vs.id}.filters`, vs.gridType, columns, vs.store, vs.viewType);
      const groupers = loadGroupers(`${vs.id}.groupers`, vs.gridType, columns, vs.store, vs.viewType);
      const columnChanges = loadColumnChanges(`${vs.id}.column-changes`, vs.gridType, vs.viewType, this.context);
      const timelineChanges = loadTimeLineChanges(`${vs.id}.timeline-changes`, vs.gridType, vs.viewType, this.context);
      return accum.concat(filters, sorters, groupers, columnChanges, timelineChanges);
    }, []);
    if (viewStateData.length) {
      list.store.data = viewStateData;
    } else {
      list.store.data = [];
    }
    this.updateListTitle(viewStateData.length);
  }

  updateListTitle(hide) {
    if (hide) {
      this.title = null;
    } else {
      this.title = "<span class='!p-0 w-full text-center flex items-center justify-center !min-h-[auto]'>No View State</span>";
    }
  }

  canDeleteViewState() {
    const user = this.context.user;
    const viewRecord = this.record;
    if (viewRecord.isSharedView && user.id !== viewRecord.creatorUserId) {
      MessageDialog.alert({ title: "Shared View", message: `Shared View created by ${defaultCellRenderer({ value: viewRecord.creatorUserName })}. Only creator of this shared view or a board admin can delete this view.` });
      return false;
    }
    return true;
  }

  loadRecord(record, context, otherParams = {}) {
    this.record = record;
    this.context = context;
    const { canUseRecordViewState, hideRemoveButtons } = otherParams;
    if (canUseRecordViewState || hideRemoveButtons) this.element.classList.add(`view-inspector-hide-remove-button`)
    if (typeof record.viewState === "object" && Object.keys(record.viewState).length) {
      let viewState;
      if (otherParams.canUseRecordViewState) {
        viewState = (record.isDefaultView ? record.viewState.customViewState : record.viewState) ?? {}
      } else {
        viewState = context.viewManager.getCurrentDefaultViewStates();
      }
      const
        viewStates = Object.entries(viewState)
        .filter(([_viewType, viewState]) => Boolean(viewState.store))
        .map(([viewType, viewState]) => {
          const viewConfig = STATE_ID_TO_VIEW_NAME[`${record.viewType}-${viewType}`];
          return ({
            id: `${viewType}.store`,
            gridType: viewConfig.title,
            gridColumns: viewConfig.config.columns,
            store: viewState.store,
            viewType,
          })
        });
      if (viewStates.length) {
        this.updateGridData(viewStates);
      } else {
        this.updateListTitle(false);
      }
    } else {
      this.updateListTitle(false);
    }
  }

  resetWidgetToDefaultState(widget, keyObj) {
    if (typeof keyObj === "object") {
      widget.state = {...widget.state, ...keyObj}
      return;
    }
    widget.state = { ...widget.state, [keyObj]: widget.defaultState[keyObj] }
  }

  removeStateFromWidget(record) {
    const records = Array.isArray(record) ? record : [record];
    const pageRefs = this.context.viewManager.pageViewsRef;
    const actionMap = {
      grouper: (widget, itemPath) => widget.store.removeGrouper(itemPath.field),
      sorter: (widget, itemPath) => widget.store.removeSorter(itemPath.field),
      filter: (widget, itemPath) => widget.store.removeFilter(itemPath.id),
      "column-changes": (widget) => this.resetWidgetToDefaultState(widget, "columns"),
      "timeline-zoom-level-change": (widget) => this.resetWidgetToDefaultState(widget, "zoomLevel"),
      "timeline-tick-size-change": (widget) => this.resetWidgetToDefaultState(widget, "tickSize"),
      "timeline-features-change": (widget) => this.resetWidgetToDefaultState(widget, { features: widget.swivuSchedulerDefaultState.features })
    };

    records.forEach(rec => {
      const { itemPath, viewType } = rec;
      const widget = pageRefs[viewType];
      actionMap[itemPath.type](widget, itemPath);
    })

    this.store.remove(records);
    this.updateListTitle(this.store.data.length);
  }

  handleItemClick({ record, event }) {
    const { target } = event;
    const button = target.classList.contains(REMOVE_BUTTON_CLS) ? target : target.closest(`.${REMOVE_BUTTON_CLS}`);
    if (button) {
      this.removeStateFromWidget(record.isGroupHeader ? record.groupChildren : record);
    }
  }
}

ViewStateInspectionView.initClass();
